// ===========================================
// Student Upcoming Classes Component
// ===========================================

import { Alert, Box, Chip, List, Paper, Skeleton, Typography } from '@mui/material';
import { format } from 'date-fns';
import { CalendarEvent } from '../../../api/hybridBooking.api';

interface Props {
  studentName: string;
  calendarEvents: CalendarEvent[];
  isLoading: boolean;
}

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Student Upcoming Classes (filtered from calendar events)
 */
export default function StudentUpcomingClasses({
  studentName,
  calendarEvents,
  isLoading,
}: Props) {
  if (isLoading) {
    return <Skeleton variant="rectangular" height={200} />;
  }

  // Filter events for this student and get unique lessons (by lessonId)
  const studentEvents = calendarEvents.filter(
    (event) =>
      event.resource.studentName === studentName &&
      event.resource.type !== 'HYBRID_PLACEHOLDER'
  );

  // Get unique lessons by lessonId
  const uniqueLessons = new Map<string, CalendarEvent>();
  studentEvents.forEach((event) => {
    if (event.resource.lessonId && !uniqueLessons.has(event.resource.lessonId)) {
      uniqueLessons.set(event.resource.lessonId, event);
    }
  });

  const lessons = Array.from(uniqueLessons.values());

  if (lessons.length === 0) {
    return <Alert severity="info">No classes scheduled.</Alert>;
  }

  // Group by day of week
  const groupedLessons = lessons.reduce(
    (acc, event) => {
      const day = event.start.getDay();
      if (!acc[day]) acc[day] = [];
      acc[day].push(event);
      return acc;
    },
    {} as Record<number, CalendarEvent[]>
  );

  return (
    <List disablePadding>
      {Object.entries(groupedLessons)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([day, dayEvents]) => (
          <Box key={day} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {DAY_NAMES[Number(day)]}
            </Typography>
            {dayEvents.map((event) => (
              <Paper key={event.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {event.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format(event.start, 'h:mm a')} - {format(event.end, 'h:mm a')}
                      {event.resource.roomName && ` | ${event.resource.roomName}`}
                    </Typography>
                  </Box>
                  {event.resource.lessonTypeName && (
                    <Chip
                      label={event.resource.lessonTypeName}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        ))}
    </List>
  );
}
